import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import ConfirmModal from '../components/ConfirmModal';
import './Submissions.css';

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Get today's date in YYYY-MM-DD format
const getToday = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

function Submissions() {
  const [submissions, setSubmissions] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [dateFrom, setDateFrom] = useState(getToday());
  const [dateTo, setDateTo] = useState(getToday());
  const [actionFilter, setActionFilter] = useState('');
  const [affiliateFilter, setAffiliateFilter] = useState('');
  const [filterOptions, setFilterOptions] = useState({ affiliates: [], actions: [] });
  const [globalFilter, setGlobalFilter] = useState('');
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, submissionId: null });
  
  const debouncedSearch = useDebounce(globalFilter, 300);

  useEffect(() => {
    fetchFilterOptions();
    fetchSubmissions();
  }, []);

  // Trigger search when debounced value changes (2+ chars or empty)
  useEffect(() => {
    if (debouncedSearch.length >= 2 || debouncedSearch.length === 0) {
      fetchSubmissions(1);
    }
  }, [debouncedSearch]);

  const fetchFilterOptions = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/submissions/filters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFilterOptions(data);
      }
    } catch (error) {
      console.error('Failed to load filter options');
    }
  };

  const fetchSubmissions = async (page = 1) => {
    const token = localStorage.getItem('admin_token');
    setLoading(true);
    
    try {
      let url = `${API_URL}/submissions?page=${page}&limit=${pagination.limit}&dateFrom=${dateFrom}&dateTo=${dateTo}`;
      if (actionFilter) url += `&action=${encodeURIComponent(actionFilter)}`;
      if (affiliateFilter) url += `&affiliate=${encodeURIComponent(affiliateFilter)}`;
      if (globalFilter.length >= 2) url += `&search=${encodeURIComponent(globalFilter)}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSubmissions(data.submissions);
        setPagination(prev => ({
          ...prev,
          page,
          ...data.pagination
        }));
      } else {
        toast.error('Failed to load submissions');
      }
    } catch (error) {
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchSubmissions(1);
  };

  const openDeleteConfirm = (id) => {
    setConfirmModal({ isOpen: true, submissionId: id });
  };

  const closeDeleteConfirm = () => {
    setConfirmModal({ isOpen: false, submissionId: null });
  };

  const handleDelete = async () => {
    const id = confirmModal.submissionId;
    if (!id) return;

    const token = localStorage.getItem('admin_token');

    try {
      const response = await fetch(`${API_URL}/submissions/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Submission deleted');
        fetchSubmissions(pagination.page);
      } else {
        toast.error('Failed to delete');
      }
    } catch (error) {
      toast.error('Delete failed');
    } finally {
      closeDeleteConfirm();
    }
  };

  const handleExport = async () => {
    const token = localStorage.getItem('admin_token');
    setExporting(true);

    try {
      let url = `${API_URL}/submissions/export?dateFrom=${dateFrom}&dateTo=${dateTo}`;
      if (actionFilter) url += `&action=${encodeURIComponent(actionFilter)}`;
      if (affiliateFilter) url += `&affiliate=${encodeURIComponent(affiliateFilter)}`;
      if (globalFilter.length >= 2) url += `&search=${encodeURIComponent(globalFilter)}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `submissions-${dateFrom}-to-${dateTo}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(blobUrl);
        toast.success('Export successful');
      } else {
        toast.error('Export failed');
      }
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateString) => {
    // Parse date string directly without timezone conversion
    // MySQL returns: "YYYY-MM-DDTHH:MM:SS.000Z" or "YYYY-MM-DD HH:MM:SS"
    if (!dateString) return '';
    
    // Extract date parts directly from the string
    const str = dateString.replace('T', ' ').replace('Z', '').split('.')[0];
    const [datePart, timePart] = str.split(' ');
    const [year, month, day] = datePart.split('-');
    const [hours, minutes, seconds] = (timePart || '00:00:00').split(':');
    
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchSubmissions(newPage);
    }
  };

  return (
    <div className="submissions-page">
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title="Delete Submission"
        message="Are you sure you want to delete this submission? This action cannot be undone."
        onConfirm={handleDelete}
        onCancel={closeDeleteConfirm}
      />

      {/* Search Filters */}
      <div className="search-filters">
        <div className="filter-group">
          <label>Date From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>Date To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>Action</label>
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
          >
            <option value="">All Actions</option>
            {filterOptions.actions.map((action, index) => (
              <option key={index} value={action}>
                {action === 'whatsapp' ? 'WhatsApp' :
                 action === 'telegram' ? 'Telegram' :
                 action === 'download' ? 'Download' :
                 action === 'official_site' ? 'Official Site' :
                 action === 'customer_service' ? 'Customer Services' : action}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Affiliate</label>
          <select
            value={affiliateFilter}
            onChange={(e) => setAffiliateFilter(e.target.value)}
          >
            <option value="">All Affiliates</option>
            {(filterOptions.affiliates || []).map((code, index) => (
              <option key={index} value={code}>{code}</option>
            ))}
          </select>
        </div>
        <button 
          className="btn btn-primary"
          onClick={handleSearch}
          disabled={loading}
        >
          🔍 Search
        </button>
      </div>

      {/* Results Info */}
      <div className="results-info">
        <p className="total-count">Total: {pagination.total} submissions</p>
        <div className="global-filter">
          <span className="filter-icon">🔍</span>
          <input
            type="text"
            placeholder="Filter (min 2 chars)..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
          />
        </div>
        <button 
          className="btn btn-success"
          onClick={handleExport}
          disabled={exporting}
        >
          {exporting ? 'Exporting...' : '📥 Export Excel'}
        </button>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="loading-container">
          <div className="loader"></div>
        </div>
      ) : (
        <>
          {/* Table */}
          {submissions.length > 0 ? (
            <div className="card table-card">
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Affiliate</th>
                      <th>Action</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.map((submission) => (
                      <tr key={submission.id}>
                        <td>{submission.id}</td>
                        <td>{submission.name}</td>
                        <td>{submission.phone}</td>
                        <td>
                          {submission.affiliate_code
                            ? <span className="affiliate-code">{submission.affiliate_code}</span>
                            : <span className="muted">-</span>}
                        </td>
                        <td>
                          <span className={`action-badge ${submission.action_type || 'whatsapp'}`}>
                            {submission.action_type === 'telegram' ? '✈️ Telegram' :
                             submission.action_type === 'download' ? '📥 Download' :
                             submission.action_type === 'official_site' ? '🌐 Official Site' :
                             submission.action_type === 'customer_service' ? '🎧 Customer Services' : '💬 WhatsApp'}
                          </span>
                        </td>
                        <td>{formatDate(submission.created_at)}</td>
                        <td>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => openDeleteConfirm(submission.id)}
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="card empty-state">
              <span className="empty-icon">📭</span>
              <h3>No submissions found</h3>
              <p>No submissions for the selected date range</p>
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-secondary"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                ← Previous
              </button>
              
              <span className="page-info">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              
              <button
                className="btn btn-secondary"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Submissions;
